/**
 * Date and time rendering for every app.
 *
 * Two rules, both of which the tree was breaking before this module:
 *
 * 1. **The zone is Vienna, not the browser's.** Every formatter here pins
 *    {@link SHOP_TIME_ZONE}. A technician opening the app from anywhere
 *    else must still see the shop's wall clock, and a business date must
 *    not shift because the operator is travelling.
 * 2. **The locale comes from `localeFor(lang)`.** Not from a ternary at
 *    the call site, which is how `en-US` (month-first, 12-hour) ended up
 *    rendering dates in two of the four apps.
 *
 * The named styles below cover the shapes actually in use. Reach for
 * {@link formatWith} only when a screen genuinely needs a one-off.
 */
import { localeFor, SHOP_TIME_ZONE, type Lang } from '../i18n/lang'

/** Anything an API or a component realistically hands a formatter. */
export type DateLike = string | number | Date | null | undefined

/** Rendered in place of a missing date. */
export const EMPTY_DATE = '—'

const cache = new Map<string, Intl.DateTimeFormat>()

function formatter(lang: Lang, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${lang}|${JSON.stringify(opts)}`
  const hit = cache.get(key)
  if (hit) return hit
  const made = new Intl.DateTimeFormat(localeFor(lang), { timeZone: SHOP_TIME_ZONE, ...opts })
  cache.set(key, made)
  return made
}

function toDate(value: DateLike): Date | null {
  if (value == null || value === '') return null
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Format with an explicit options object, still Vienna-pinned and still
 * locale-resolved. For the handful of one-off shapes that don't earn a
 * named style — pass `timeZone` yourself only if you truly mean another
 * zone.
 */
export function formatWith(value: DateLike, lang: Lang, opts: Intl.DateTimeFormatOptions): string {
  const d = toDate(value)
  if (!d) return typeof value === 'string' && value !== '' ? value : EMPTY_DATE
  return formatter(lang, opts).format(d)
}

const style =
  (opts: Intl.DateTimeFormatOptions) =>
  (value: DateLike, lang: Lang): string =>
    formatWith(value, lang, opts)

/** `20.08.2026` */
export const formatDate = style({ year: 'numeric', month: '2-digit', day: '2-digit' })

/** `20.08.26` */
export const formatDateShort = style({ year: '2-digit', month: '2-digit', day: '2-digit' })

/** `20. Aug 2026` */
export const formatDateMed = style({ year: 'numeric', month: 'short', day: '2-digit' })

/** `20. Aug` */
export const formatDayMonth = style({ month: 'short', day: '2-digit' })

/** `August 2026` */
export const formatMonthYear = style({ year: 'numeric', month: 'long' })

/** `Donnerstag, 20. August` */
export const formatDateLong = style({ weekday: 'long', month: 'long', day: 'numeric' })

/** `Do` */
export const formatWeekdayShort = style({ weekday: 'short' })

/** `16:30` — 24-hour in both languages; the shop floor reads a 24-hour clock. */
export const formatTime = style({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

/** `20.08.2026, 16:30` */
export const formatDateTime = style({
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})

/** `20.08.26, 16:30` */
export const formatDateTimeShort = style({
  year: '2-digit', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})

/** `20. Aug 2026, 16:30` */
export const formatDateTimeMed = style({
  year: 'numeric', month: 'short', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})

/** `20. Aug, 16:30` */
export const formatDayMonthTime = style({
  month: 'short', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})

/** `Do, 20. Aug, 16:30` */
export const formatWeekdayDateTime = style({
  weekday: 'short', month: 'short', day: 'numeric',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})

/* ------------------------------------------------------------------ */
/* Vienna calendar days                                                */
/* ------------------------------------------------------------------ */

const DAY_KEY = new Intl.DateTimeFormat('en-CA', {
  timeZone: SHOP_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/**
 * The Vienna calendar day an instant falls on, as `YYYY-MM-DD`.
 *
 * Deliberately not `toISOString().slice(0, 10)` — that answers in UTC, so
 * an evening in Vienna reports tomorrow and any comparison against a
 * business date is a day out. `en-CA` is used purely because its date
 * order already *is* `YYYY-MM-DD`.
 */
export function viennaDayKey(value: DateLike = new Date()): string {
  const d = toDate(value)
  return d ? DAY_KEY.format(d) : ''
}

/**
 * Do two instants fall on the same Vienna calendar day?
 *
 * The "show a time if it happened today, a date otherwise" rule several
 * tables use. Comparing `getFullYear()/getMonth()/getDate()` instead asks
 * the *viewer's* machine what day it is, so just after Vienna midnight a
 * manager abroad sees yesterday's row labelled with a bare time and reads
 * it as just-now.
 */
/**
 * Is a **floating calendar date** the shop's today?
 *
 * Distinct from {@link isSameViennaDay}, which compares two instants.
 * Calendar grids build their cells as local-midnight `Date`s standing for
 * a calendar day, with no instant meaning — running one of those through
 * a timezone conversion would shift it (local midnight in Tokyo is the
 * previous afternoon in Vienna). So this compares the cell's own calendar
 * fields against the shop's current day key instead.
 */
export function isViennaToday(calendarDate: Date): boolean {
  if (Number.isNaN(calendarDate.getTime())) return false
  const y = calendarDate.getFullYear()
  const m = String(calendarDate.getMonth() + 1).padStart(2, '0')
  const d = String(calendarDate.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}` === viennaDayKey()
}

export function isSameViennaDay(a: DateLike, b: DateLike): boolean {
  // Guard nullish explicitly: `viennaDayKey` defaults a missing argument
  // to *now*, which would make a missing side compare equal to today.
  const da = toDate(a)
  const db = toDate(b)
  if (!da || !db) return false
  return viennaDayKey(da) === viennaDayKey(db)
}

const VIENNA_HOUR = new Intl.DateTimeFormat('en-GB', {
  timeZone: SHOP_TIME_ZONE,
  hourCycle: 'h23',
  hour: '2-digit',
})

/**
 * The hour-of-day (0–23) an instant falls on in Vienna.
 *
 * A real accessor rather than slicing {@link formatTime} output: bucketing
 * by hour is a calculation, not a rendering, and it must not depend on the
 * UI language or on a formatter's padding staying two digits.
 *
 * Read via `formatToParts` rather than parsing the formatted string —
 * an hour-only format is not safe to `Number()`, because some locales
 * append a unit (de-AT renders `"09 Uhr"`).
 */
export function viennaHourOf(value: DateLike): number {
  const d = toDate(value)
  if (!d) return 0
  const hour = VIENNA_HOUR.formatToParts(d).find((p) => p.type === 'hour')
  return hour ? Number(hour.value) : 0
}

/**
 * Minutes since Vienna midnight, e.g. `16:30` → `990`.
 *
 * For positioning against a wall-clock grid. The calendar's rows are the
 * shop's opening hours in Vienna, so a position derived from
 * `date.getHours()` — the *viewer's* clock — puts the line in the wrong
 * row for anyone outside Austria, every hour of the day rather than just
 * around midnight.
 *
 * Seconds are included as a fraction so a ticking indicator moves
 * smoothly rather than jumping a whole minute.
 */
/**
 * Vienna wall-clock time as `HH:mm`, locale-independent.
 *
 * {@link formatTime} is the one to use for anything a person reads — it
 * goes through the UI language. This is for values that must not vary
 * with it: an API query parameter, a CSV cell, a grid key.
 */
export function viennaClockTime(value: DateLike): string {
  const total = Math.floor(viennaMinutesSinceMidnight(value))
  const h = Math.floor(total / 60)
  return `${String(h).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

export function viennaMinutesSinceMidnight(value: DateLike): number {
  const d = toDate(value)
  if (!d) return 0
  const parts: Record<string, string> = {}
  for (const { type, value: v } of PARTS.formatToParts(d)) parts[type] = v
  return Number(parts.hour) * 60 + Number(parts.minute) + Number(parts.second) / 60
}

const PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: SHOP_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
})

/** Vienna's UTC offset in ms at a given instant (positive east of UTC). */
function offsetAt(ts: number): number {
  const p: Record<string, string> = {}
  for (const { type, value } of PARTS.formatToParts(new Date(ts))) p[type] = value
  const asUtc = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour), Number(p.minute), Number(p.second),
  )
  return asUtc - ts
}

/**
 * The instant at which a Vienna wall-clock time occurs.
 *
 * Resolved in two passes because the offset depends on the instant we are
 * still solving for; the second pass is what keeps the DST changeover
 * nights correct.
 */
function viennaWallClock(y: number, m: number, d: number, hh = 0, mm = 0, ss = 0): Date {
  const naive = Date.UTC(y, m - 1, d, hh, mm, ss)
  const first = naive - offsetAt(naive)
  const second = naive - offsetAt(first)
  return new Date(second)
}

/**
 * Split a `YYYY-MM-DD` key into its parts.
 *
 * Written out rather than destructured off `split()` because two of the
 * consuming apps compile with `noUncheckedIndexedAccess`, under which
 * array indexing yields `number | undefined`.
 */
function dayParts(dayKey: string): { y: number; m: number; d: number } {
  const [ys = '', ms = '', ds = ''] = dayKey.split('-')
  return { y: Number(ys), m: Number(ms), d: Number(ds) }
}

/**
 * A `Date` → the `YYYY-MM-DDTHH:mm` string `<input type="datetime-local">`
 * expects, expressed in **Vienna** wall-clock time.
 *
 * Deliberately not the Date's local fields. `datetime-local` carries no
 * zone, so whatever this writes is what the operator reads back — and a
 * manager working from another country picking "14:00" for a Vienna slot
 * means 14:00 in the shop, not 14:00 where they happen to be sitting.
 * Pair with {@link viennaDateTimeToInstant}; the round trip only holds if
 * both ends agree on the zone.
 */
export function toViennaDateTimeValue(value: DateLike): string {
  const key = viennaDayKey(value)
  return key === '' ? '' : `${key}T${viennaClockTime(value)}`
}

/**
 * The instant a `YYYY-MM-DDTHH:mm` picker value refers to, reading it as
 * Vienna wall-clock time. Inverse of {@link toViennaDateTimeValue}.
 */
export function viennaDateTimeToInstant(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value.trim())
  if (!m) return null
  const [, y, mo, d, hh, mm] = m
  const at = viennaWallClock(Number(y), Number(mo), Number(d), Number(hh), Number(mm))
  return Number.isNaN(at.getTime()) ? null : at
}

/** Vienna midnight starting the given `YYYY-MM-DD`. */
export function startOfViennaDay(dayKey: string): Date {
  const { y, m, d } = dayParts(dayKey)
  return viennaWallClock(y, m, d)
}

/**
 * The `[from, to)` instants bounding a Vienna calendar day, as ISO strings
 * for an API query. `to` is the next day's midnight, so the window is
 * exactly 24h except across a DST change — where it is correctly 23h or 25h.
 */
export function viennaDayWindow(dayKey: string): { from: string; to: string } {
  const start = startOfViennaDay(dayKey)
  const { y, m, d } = dayParts(dayKey)
  // Round-trip through Date.UTC so `d + 1` rolls month and year correctly.
  const next = new Date(Date.UTC(y, m - 1, d + 1))
  const end = viennaWallClock(
    next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(),
  )
  return { from: start.toISOString(), to: end.toISOString() }
}
